import React, { useState } from "react";
import { toast } from "sonner";
import { Tag, X } from "lucide-react";
import { useActivePromo } from "@/lib/promo";

const REPEATS = [0, 1, 2, 3];

/**
 * Thin promotional strip that rides above the navigation on every page. The offer is
 * read live from the coupon the admin flagged as featured, so switching it off in
 * Admin - Offers removes this bar on the next page load. Clicking anywhere on the
 * strip copies the code; the X hides it for the rest of the browsing session.
 */
export const PromoBar = () => {
  const promo = useActivePromo();
  const [hidden, setHidden] = useState(
    () => sessionStorage.getItem("promo_bar_hidden") === "1"
  );

  if (!promo || hidden) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      toast.success("Code copied", { description: `Use ${promo.code} at checkout` });
    } catch {
      toast.info(`Use code ${promo.code} at checkout`);
    }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem("promo_bar_hidden", "1");
    setHidden(true);
  };

  const unit = (key) => (
    <span key={key} className="flex items-center gap-3 px-6 whitespace-nowrap">
      <Tag size={12} className="text-secondary-foreground/70 shrink-0" />
      <span className="text-[12.5px] tracking-wide">{promo.benefit}</span>
      <span className="text-[11px] uppercase tracking-[0.2em] opacity-70">use code</span>
      <span className="text-[12.5px] font-semibold tracking-[0.18em] px-2 py-0.5 rounded-full bg-background/15 border border-background/25">
        {promo.code}
      </span>
      <span aria-hidden className="opacity-40">&middot;</span>
    </span>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={copy}
      onKeyDown={(e) => { if (e.key === "Enter") copy(); }}
      title={`Tap to copy ${promo.code}`}
      data-testid="promo-bar"
      data-promo-code={promo.code}
      className="relative h-9 flex items-center overflow-hidden cursor-pointer select-none bg-primary text-primary-foreground border-b border-primary/40"
    >
      <div className="promo-track" data-testid="promo-bar-track">
        {[0, 1].map((half) => (
          <div key={half} className="flex shrink-0" aria-hidden={half === 1}>
            {REPEATS.map((i) => unit(`${half}-${i}`))}
          </div>
        ))}
      </div>

      <div className="absolute inset-y-0 right-0 flex items-center pl-8 pr-3 bg-gradient-to-l from-primary via-primary to-transparent">
        <button
          onClick={dismiss}
          aria-label="Hide this offer"
          data-testid="promo-bar-close"
          className="p-1 rounded-full text-primary-foreground/70 hover:text-primary-foreground hover:bg-background/15 transition-colors"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
};

export default PromoBar;
